import {
  API_ROUTES,
  interpolateRoute,
  type ApiResponse,
  type NodeActionGroup,
  type NodeActionGroupUsage,
  type SaveNodeActionGroupDto,
} from '@super/shared-types'
import { ApiError, BaseApiClient } from './base-client'
import { withErrorHandling } from '../utils'

/**
 * The outcome of a delete call that the UI must branch on. `in_use` carries
 * the usage count from the 409 body so the handler can tell the admin which
 * graphs block the delete.
 */
export type DeleteActionResult =
  | { ok: true }
  | { ok: false; code: 'in_use'; usageCount: number }
  | { ok: false; code: 'error' }

export class ActionsApiClient extends BaseApiClient {
  async getActions(): Promise<NodeActionGroup[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeActionGroup[]>>(
          API_ROUTES.ACTION_GROUPS.GET_ALL,
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching actions',
      },
    )
    return result ?? []
  }

  async getActionById(id: string): Promise<NodeActionGroup | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeActionGroup>>(
          interpolateRoute(API_ROUTES.ACTION_GROUPS.GET, { id }),
        )
        return response.data ?? null
      },
      {
        on404: 'ignore',
        errorContext: 'Error fetching action',
      },
    )
  }

  async getActionUsage(id: string): Promise<NodeActionGroupUsage | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeActionGroupUsage>>(
          interpolateRoute(API_ROUTES.ACTION_GROUPS.GET_USAGE, { id }),
        )
        return response.data ?? null
      },
      {
        on404: 'ignore',
        errorContext: 'Error fetching action usage',
      },
    )
  }

  /** Batch usage counts for a set of shared-action ids. Missing ids map to 0. */
  async getActionUsageCounts(
    ids: readonly string[],
  ): Promise<Record<string, number>> {
    if (ids.length === 0) return {}
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<Record<string, number>>
        >(
          `${API_ROUTES.ACTION_GROUPS.GET_USAGE_COUNTS}?ids=${encodeURIComponent(ids.join(','))}`,
        )
        return response.data ?? {}
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching action usage counts',
      },
    )
    return result ?? {}
  }

  async createAction(
    dto: SaveNodeActionGroupDto,
  ): Promise<NodeActionGroup | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeActionGroup>>(
          API_ROUTES.ACTION_GROUPS.CREATE,
          { method: 'POST', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error creating action' },
    )
  }

  async updateAction(
    id: string,
    dto: SaveNodeActionGroupDto,
  ): Promise<NodeActionGroup | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeActionGroup>>(
          interpolateRoute(API_ROUTES.ACTION_GROUPS.UPDATE, { id }),
          { method: 'PUT', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error updating action' },
    )
  }

  async deleteAction(id: string): Promise<DeleteActionResult> {
    try {
      await this.request(
        interpolateRoute(API_ROUTES.ACTION_GROUPS.DELETE, { id }),
        {
          method: 'DELETE',
        },
      )
      return { ok: true }
    } catch (error) {
      return parseDeleteError(error, 'Error deleting action')
    }
  }

  /** [ADMIN] List shared actions for any company. */
  async getActionsForCompanyByAdmin(
    companyId: string,
  ): Promise<NodeActionGroup[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeActionGroup[]>>(
          `${API_ROUTES.ADMIN.ACTION_GROUPS.GET_BY_COMPANY}?companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: `Error fetching actions for company ${companyId}`,
      },
    )
    return result ?? []
  }

  /** [ADMIN] Get a shared action for a company. */
  async getActionForCompanyByAdmin(
    id: string,
    companyId: string,
  ): Promise<NodeActionGroup | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeActionGroup>>(
          `${interpolateRoute(API_ROUTES.ADMIN.ACTION_GROUPS.GET, { id })}?companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? null
      },
      { on404: 'ignore', errorContext: 'Error fetching action' },
    )
  }

  /** [ADMIN] Count graph nodes referencing a shared action. */
  async getActionUsageForCompanyByAdmin(
    id: string,
    companyId: string,
  ): Promise<NodeActionGroupUsage | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeActionGroupUsage>>(
          `${interpolateRoute(API_ROUTES.ADMIN.ACTION_GROUPS.GET_USAGE, { id })}?companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? null
      },
      { on404: 'ignore', errorContext: 'Error fetching action usage' },
    )
  }

  /** [ADMIN] Batch usage counts for shared actions. */
  async getActionUsageCountsForCompanyByAdmin(
    ids: readonly string[],
    companyId: string,
  ): Promise<Record<string, number>> {
    if (ids.length === 0) return {}
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<Record<string, number>>
        >(
          `${API_ROUTES.ADMIN.ACTION_GROUPS.GET_USAGE_COUNTS}?ids=${encodeURIComponent(ids.join(','))}&companyId=${encodeURIComponent(companyId)}`,
        )
        return response.data ?? {}
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching action usage counts',
      },
    )
    return result ?? {}
  }

  /** [ADMIN] Create a shared action for a company. */
  async createActionForCompany(
    companyId: string,
    dto: SaveNodeActionGroupDto,
  ): Promise<NodeActionGroup | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeActionGroup>>(
          `${API_ROUTES.ADMIN.ACTION_GROUPS.CREATE}?companyId=${encodeURIComponent(companyId)}`,
          { method: 'POST', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error creating action' },
    )
  }

  /** [ADMIN] Update a shared action. */
  async updateActionForCompany(
    id: string,
    companyId: string,
    dto: SaveNodeActionGroupDto,
  ): Promise<NodeActionGroup | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<NodeActionGroup>>(
          `${interpolateRoute(API_ROUTES.ADMIN.ACTION_GROUPS.UPDATE, { id })}?companyId=${encodeURIComponent(companyId)}`,
          { method: 'PUT', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error updating action' },
    )
  }

  /** [ADMIN] Delete a shared action. */
  async deleteActionForCompany(
    id: string,
    companyId: string,
  ): Promise<DeleteActionResult> {
    try {
      await this.request(
        `${interpolateRoute(API_ROUTES.ADMIN.ACTION_GROUPS.DELETE, { id })}?companyId=${encodeURIComponent(companyId)}`,
        { method: 'DELETE' },
      )
      return { ok: true }
    } catch (error) {
      return parseDeleteError(error, 'Error deleting action')
    }
  }
}

function parseDeleteError(error: unknown, context: string): DeleteActionResult {
  if (error instanceof ApiError && error.status === 409) {
    const body = error.body as { code?: string; usageCount?: number } | null
    if (body?.code === 'action_in_use') {
      return { ok: false, code: 'in_use', usageCount: body.usageCount ?? 0 }
    }
  }
  console.error(`${context}:`, error)
  return { ok: false, code: 'error' }
}

export const actionsApiClient = new ActionsApiClient()
